/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';

import { Field } from 'formik';
import { kebabCase, get } from 'lodash';

import { Select } from '@agconnections/grow-ui';

import {
  createPartiallyAppliedEventHandlerWithTransform,
  getEventSelectedKey
} from 'utilities/formHelpers';

/**
 * wraps form components in a Formik Field. This allow thes components to
 * be integrated into Formik with little overhead, byt grabbing the Formik
 * Bag, from the implicit context.
 * @param {React.Component} WrappedComponent - the component to be wrapped
 * @param {String} formName - form name used for generating component ids
 */
const withFormikField = (WrappedComponent, formName) => {
  const FormikFieldWrapper = ({
    name,
    id = '',
    children = null,
    ariaLabel = null,
    onChange = null,
    attachRight = null,
    attachRightSelectField = null,
    ...props
  }) => {
    const generatedId = `${formName}-${kebabCase(name)}`;
    const handleChange = (field, form) => event => {
      // masked inputs do not return a real event with an event.target.name
      const evt = event;
      if (!evt.target.name) {
        evt.target.name = field.name;
      }
      if (onChange) {
        onChange(evt, form);
      }
      field.onChange(evt);
    };
    const handleRightAttachment = form => {
      if (attachRightSelectField) {
        const selectedValue = get(form.values, attachRightSelectField.name, '');
        const handleSelectChange = createPartiallyAppliedEventHandlerWithTransform(
          form.setFieldValue,
          getEventSelectedKey
        );
        return (
          <Select
            id={`${id}-attach-select`}
            name={attachRightSelectField.name}
            items={attachRightSelectField.items}
            value={selectedValue}
            onChange={handleSelectChange(attachRightSelectField.name)}
            isAttachment
          />
        );
      }
      return attachRight;
    };

    return (
      <Field name={name}>
        {({ field, meta, form }) => (
          <WrappedComponent
            ariaLabel={ariaLabel || id}
            id={id || generatedId}
            {...field}
            error={meta.error}
            {...props}
            attachRight={handleRightAttachment(form)}
            onChange={handleChange(field, form)}
          >
            {children}
          </WrappedComponent>
        )}
      </Field>
    );
  };
  FormikFieldWrapper.propTypes = {
    ariaLabel: PropTypes.string,
    children: PropTypes.node,
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    attachRight: PropTypes.node,
    attachRightSelectField: PropTypes.shape({
      name: PropTypes.string,
      items: PropTypes.arrayOf(PropTypes.object)
    })
  };
  return FormikFieldWrapper;
};

export default withFormikField;
